//@flow
import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./style.scss";

export type ContextMenu2PositionType = { left: number, top: number };
export type ContextMenuConfigType = Array<MenuItemType>;

import { MenuItem } from "./menu-item";
export { MenuItem } from "./menu-item";
import type { MenuItemType } from "./menu-item";
export type { MenuItemType } from "./menu-item";

export type Props = {
  clickTriggerEl?: React$Element<any>, // If you want to show/hide the menu onClick
  mouseOverTriggerEl?: React$Element<any>, // If you want to show/hide with onMouseOver (hover)
  // You should not use both triggers
  position?: null | ContextMenu2PositionType | false, // null - never touched; positionType = currently visible coordinates; false = not visible
  align?: "auto" | "top" | "left" | "bottom" | "right",
  onOutsideClick?: Function,
  onHideContextMenu?: Function,
  onShowContextMenu?: Function,
  children?: React$Element<any>,
  config?: MenuItemType[],
};

export const ContextMenu = React.forwardRef((props, ref) => {
  if (!props.config && !props.children)
    console.error(
      "The context menu you are trying to spawn should have children OR config and you missed to provide one of them",
    );
  const menuContainerRef = useRef(null); // Use the content as pivot point for the menu
  const triggerElRef = useRef(null); // Use the content as pivot point for the menu
  const alignMenu = props.align || "left";

  let portalNode = null;

  const triggerEl = props.clickTriggerEl || props.mouseOverTriggerEl;

  const [position, setPosition]: [{ top: number, left: number }, Function] = useState(null);
  const [offset, setOffset]: [{ x: number, y: number }, Function] = useState({ x: 0, y: 0 });

  useEffect(() => {
    !!props.position ? showContextMenu(props.position.left, props.position.top) : hideContextMenu();
    document.body && document.body.addEventListener("mousedown", isOutsideClick);
    return () => {
      document.body && document.body.removeEventListener("mousedown", isOutsideClick);
    };
  }, [props.position]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (position && menuContainerRef.current) {
        let off = { x: 0, y: 0 };
        const alignOff = alignOffset(menuContainerRef);
        if (alignOff.x || alignOff.y) {
          off.x += alignOff.x;
          off.y += alignOff.y;
        }

        const outOfBounds = getComponentOutOfBounds(menuContainerRef);
        if (outOfBounds.x || outOfBounds.y) {
          off.x += outOfBounds.x - alignOff.x / 2;
          off.y += outOfBounds.y - alignOff.y / 2;
        }

        setOffset(_ => off);
      }
    }, 1);
    return () => {
      clearTimeout(t);
    };
  }, [position]);

  function alignOffset(menuContainerRef): { x: number, y: number } {
    let x = 0;
    let y = 0;
    if (!alignMenu) return { x: x, y: y };
    const menuContainerRect = menuContainerRef.current.getBoundingClientRect();

    const al = alignMenu.split(" ");

    al.forEach(a => {
      switch (a) {
        case "top":
          y -= menuContainerRect.height;
          break;
        case "right":
          y -= menuContainerRect.height / 2;
          break;
        case "bottom":
          x -= menuContainerRect.width / 2;
          break;
        case "left":
          x -= menuContainerRect.width / 2;
          break;
      }
    });
    return { x: Math.ceil(x), y: Math.ceil(y) };
  }

  function hideContextMenu() {
    props.onHideContextMenu && props.onHideContextMenu();
    setPosition(_ => false);
    setOffset(_ => {
      return { x: 0, y: 0 };
    });
  }

  function showContextMenu(x, y) {
    if (x && y) {
      setPosition(_ => {
        return { top: y, left: x };
      });
      props.onShowContextMenu && props.onShowContextMenu();
    }
  }

  function handleTriggerOnMouseEvent(triggerElem: React.MouseEvent) {
    return function(e: React.MouseEvent) {
      if (!position) {
        const triggerRect = triggerElRef.current.getBoundingClientRect();

        const triggerRight = Math.ceil(triggerRect.right);
        const triggerBottom = Math.ceil(triggerRect.bottom);
        const triggerWidth = Math.ceil(triggerRect.width);
        const triggerHeight = Math.ceil(triggerRect.height);

        // Lets align the menu in the middle of the component
        const y = triggerBottom - triggerHeight / 2;
        const x = triggerRight - triggerWidth / 2;

        showContextMenu(x, y);
      }
    };
  }

  function isOutsideClick(event: React.MouseEvent) {
    if (!menuContainerRef.current) return;
    const containerRect = menuContainerRef.current.getBoundingClientRect();
    const isMouseInsideContainer =
      event.clientX >= containerRect.x &&
      event.clientX <= containerRect.x + containerRect.width &&
      event.clientY >= containerRect.y &&
      event.clientY <= containerRect.y + containerRect.height;

    if (isMouseInsideContainer) {
      return false;
    } else {
      hideContextMenu();
      return props.onOutsideClick && props.onOutsideClick(true);
    }
  }

  function getComponentOutOfBounds(ref, caller): Object {
    if (!ref || !ref.current) return { x: 0, y: 0 };
    const rect = ref.current.getBoundingClientRect();
    const { innerHeight, innerWidth } = window;

    let res = {};
    if (rect.x + rect.width > innerWidth) {
      res.right = rect.width + rect.x - innerWidth;
      res.x = -res.right;
    }
    if (rect.x < 0) {
      res.left = rect.x;
      res.x = -res.left;
    }
    if (rect.top < 0) {
      res.top = rect.top;
      res.y = res.top;
    }
    if (rect.y + rect.height > innerHeight) {
      res.bottom = rect.y + rect.height - innerHeight;
      res.y = -res.bottom;
    }

    return res;
  }

  function handleOnMenuItemClick(onMenuItemClick: Function) {
    return function() {
      if (onMenuItemClick) {
        onMenuItemClick();
        hideContextMenu();
      }
    };
  }
  function handleOnMenuItemMouseOver(handleOnMenuItemMouseOver: Function) {
    return function() {
      handleOnMenuItemMouseOver();
    };
  }

  function cloneConfigElements(arrayOfMenuItems: MenuItemType[]) {
    const res = arrayOfMenuItems.map((menuItem: MenuItemType, itemIdx: number) => {
      if (menuItem.children) {
        return React.cloneElement(<ContextMenu />, {
          key: `${menuItem.label}-${itemIdx}`,
          config: menuItem.children,
          mouseOverTriggerEl: (
            <MenuItem icon={menuItem.icon} key={menuItem.label} label={menuItem.label} />
          ),
        });
      } else {
        return React.cloneElement(<MenuItem />, {
          onClick: menuItem.onClick && handleOnMenuItemClick(menuItem.onClick),
          key: `${menuItem.label}-${itemIdx}`,
          label: menuItem.label,
          icon: menuItem.icon,
          disabled: menuItem.disabled,
        });
      }
    });
    return res;
  }

  function cloneChildElements(children: React$Element<any>[]) {
    return React.Children.map(children, (child: Object, index: number) => {
      return React.cloneElement(child, {
        onClick: handleOnMenuItemClick(child.props.onClick),
        label: child.props.label,
        icon: child.props.icon,
        disabled: child.props.disabled,
      });
    });
  }
  let clonedTriggerEl = <></>;
  if (triggerEl) {
    clonedTriggerEl = React.cloneElement(<div>{triggerEl}</div>, {
      ref: triggerElRef,
      className: "menu-item-trigger cloned-trigger",
      "data-has-children": true,
      onMouseOver: (props.mouseOverTriggerEl && handleTriggerOnMouseEvent(clonedTriggerEl)) || null,
      onClick: (props.clickTriggerEl && handleTriggerOnMouseEvent(clonedTriggerEl)) || null,
    });
  }

  portalNode =
    position &&
    createPortal(
      <div
        style={{
          position: !!position ? "fixed" : "relative",
          top: position?.top ? `${position.top + offset.y}px` : "auto",
          left: position?.left ? `${position.left + offset.x}px` : "auto",
        }}
        className={`context-menu offset-${offset.x}-${offset.y} ${!!position ? "toggled" : ""}`}
      >
        <div ref={menuContainerRef} className="context-menu-content">
          {props.config && cloneConfigElements(props.config)}
          {props.children && cloneChildElements(props.children)}
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      {clonedTriggerEl}
      {portalNode}
    </>
  );
});
